import { isApiError, unwrap, type RoleOut } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Field, Icon, Select, Skeleton, Tooltip, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useErrorMessage } from "../lib/api-errors";
import { usePermissions } from "../session/permissions";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";
import { DeleteRoleDialog } from "./delete-role-dialog";
import { PlanGateCard } from "./plan-gate-card";
import { RoleEditor } from "./role-editor";
import { RoleList } from "./role-list";
import { RoleMembers } from "./role-members";
import { useCustomRolesAllowed, useInvalidateAccess, useMembers, usePermissionCatalogue, useRoles, useTeams } from "./use-roles";

const NEW_ROLE_ID = "new";

function PageSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[320px_1fr]" aria-hidden="true">
      <Skeleton className="h-80" />
      <div className="space-y-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-96" />
      </div>
    </div>
  );
}

function useDuplicate(onPlanGate: () => void) {
  const api = useApi();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const invalidate = useInvalidateAccess();
  const message = useErrorMessage();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (role: RoleOut) => unwrap(api.POST("/v1/roles/{role_id}/duplicate", { params: { path: { role_id: role.id } }, body: { name: t("roles.duplicateName", { name: role.name }) } })),
    onSuccess: async (copy) => {
      toast.success(t("roles.duplicated"));
      await invalidate();
      await navigate(consoleRoutes.roles(summary.slug, copy.id));
    },
    onError: (caught) => {
      if (isApiError(caught) && caught.isPlanGate) onPlanGate();
      else toast.error(message(caught));
    },
  });
}

function NewRoleStart({ roles, value, onChange }: { roles: readonly RoleOut[]; value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="card card-body">
      <Field label={t("roles.editor.startFrom")}>
        <Select value={value} onChange={(event) => { onChange(event.target.value); }}>
          <option value="">{t("roles.editor.startBlank")}</option>
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {t("roles.editor.startFromRole", { name: role.name })}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

interface RolesHeaderProps {
  canManage: boolean;
  planAllowed: boolean;
}

function RolesHeader({ canManage, planAllowed }: RolesHeaderProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight">{t("roles.title")}</h1>
        <p className="mt-1 text-fg-muted">{t("roles.subtitle")}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="secondary">
          <AppLink href={consoleRoutes.team(summary.slug)}>
            <Icon name="users" />
            {t("roles.backToTeam")}
          </AppLink>
        </Button>
        {canManage ? (
          planAllowed ? (
            <Button asChild>
              <AppLink href={consoleRoutes.roles(summary.slug, NEW_ROLE_ID)}>
                <Icon name="plus" />
                {t("roles.newRole")}
              </AppLink>
            </Button>
          ) : (
            <Tooltip content={t("roles.planGate.title")}>
              <Button aria-disabled="true" className="cursor-not-allowed opacity-60">
                <Icon name="lock" />
                {t("roles.newRole")}
              </Button>
            </Tooltip>
          )
        ) : null}
      </div>
    </div>
  );
}

/** `/w/:ws/settings/roles/:roleId?` — Amendment B custom roles (design from team.html + staff.html matrices). */
export function RolesPage() {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const roles = useRoles();
  const catalogue = usePermissionCatalogue();
  const members = useMembers();
  const teams = useTeams();
  const permissions = usePermissions();
  const plan = useCustomRolesAllowed();
  const [gated, setGated] = useState(false);
  const [startFrom, setStartFrom] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const duplicate = useDuplicate(() => { setGated(true); });
  const planAllowed = plan.allowed && !gated;
  const canManage = permissions.can("roles.manage") && !permissions.readOnly;
  const grantable = new Set(permissions.data?.workspace ?? []);
  const creating = roleId === NEW_ROLE_ID;
  const role = roles.data?.find((r) => r.id === roleId);

  const header = <RolesHeader canManage={canManage} planAllowed={planAllowed} />;

  if (roles.isPending || catalogue.isPending || permissions.isPending || plan.isPending) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        {header}
        <PageSkeleton />
      </div>
    );
  }
  if (roles.isError || catalogue.isError) {
    return (
      <div className="mx-auto max-w-7xl space-y-6">
        {header}
        <div className="card">
          <ErrorState title={t("roles.loadError")} onRetry={() => void Promise.all([roles.refetch(), catalogue.refetch()])} retryLabel={t("common.retry")} />
        </div>
      </div>
    );
  }

  const startRole = roles.data.find((r) => r.id === startFrom);
  let editor;
  if (creating && canManage && planAllowed) {
    editor = (
      <div className="space-y-6">
        <NewRoleStart roles={roles.data} value={startFrom} onChange={setStartFrom} />
        <RoleEditor key={`new-${startFrom}`} role={undefined} initialPermissions={startRole?.permissions} groups={catalogue.data} grantable={grantable} canManage planAllowed onPlanGate={() => { setGated(true); }} />
      </div>
    );
  } else if (role) {
    editor = (
      <div className="space-y-6">
        <RoleEditor
          key={role.id}
          role={role}
          groups={catalogue.data}
          grantable={grantable}
          canManage={canManage}
          planAllowed={planAllowed}
          onPlanGate={() => { setGated(true); }}
          onDuplicate={() => { duplicate.mutate(role); }}
          onDelete={() => { setDeleteOpen(true); }}
        />
        <RoleMembers role={role} members={members.data} teams={teams.data ?? []} canManage={canManage} totalPermissions={catalogue.data.reduce((n, g) => n + g.permissions.length, 0)} />
        {deleteOpen ? <DeleteRoleDialog role={role} roles={roles.data} open={deleteOpen} onOpenChange={setDeleteOpen} /> : null}
      </div>
    );
  } else {
    editor = (
      <div className="card">
        <EmptyState icon="shield-check" title={roleId ? t("roles.notFound") : t("roles.emptyTitle")} description={t("roles.emptyBody")} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {header}
      {!planAllowed ? <PlanGateCard plan={plan.plan} /> : null}
      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[320px_1fr]">
        <section className="card self-start overflow-hidden" aria-labelledby="roles-list-title">
          <div className="card-header">
            <h2 id="roles-list-title" className="card-title">
              {t("roles.listLabel")} <span className="ml-1 font-medium text-fg-subtle">{roles.data.length}</span>
            </h2>
          </div>
          <RoleList roles={roles.data} activeId={role?.id} />
        </section>
        <div className="min-w-0">{editor}</div>
      </div>
    </div>
  );
}
